// Liefertags-Berechnung mit konfigurierbarem Cutoff + Wochentag-Set.
// Werte werden aus app_settings gelesen (Cache 60s).

import { supabase } from "@/lib/supabase";

interface DeliverySettings {
  cutoffHour: number;            // 16
  deliveryWeekdays: number[];    // [1,2,3,4,5,6] (1=Mo..6=Sa, 7=So)
}

let cache: { value: DeliverySettings; ts: number } | null = null;

export async function getDeliverySettings(): Promise<DeliverySettings> {
  if (cache && Date.now() - cache.ts < 60_000) return cache.value;
  const { data, error } = await supabase
    .from("app_settings")
    .select("key,value")
    .in("key", ["cutoff_hour", "delivery_weekdays"]);
  if (error) throw error;
  const map = Object.fromEntries((data ?? []).map((r) => [r.key, r.value]));
  const value: DeliverySettings = {
    cutoffHour: Number(map["cutoff_hour"] ?? 16),
    deliveryWeekdays: Array.isArray(map["delivery_weekdays"])
      ? (map["delivery_weekdays"] as number[])
      : [1, 2, 3, 4, 5, 6],
  };
  cache = { value, ts: Date.now() };
  return value;
}

// 0 = Sonntag → 7, 1..6 wie JS getDay()
function isoWeekday(d: Date): number {
  const w = d.getDay();
  return w === 0 ? 7 : w;
}

export function nextDeliveryDate(
  now: Date,
  settings: DeliverySettings
): Date {
  const cutoffPassed = now.getHours() >= settings.cutoffHour;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() + (cutoffPassed ? 2 : 1));
  for (let i = 0; i < 14; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    if (settings.deliveryWeekdays.includes(isoWeekday(d))) return d;
  }
  throw new Error("Kein Liefertag in 14 Tagen gefunden");
}

export function listDeliveryDates(
  now: Date,
  settings: DeliverySettings,
  count = 7
): Date[] {
  const out: Date[] = [];
  let cursor = nextDeliveryDate(now, settings);
  out.push(cursor);
  while (out.length < count) {
    const next = new Date(cursor);
    next.setDate(cursor.getDate() + 1);
    for (let i = 0; i < 14; i++) {
      const d = new Date(next);
      d.setDate(next.getDate() + i);
      if (settings.deliveryWeekdays.includes(isoWeekday(d))) {
        out.push(d);
        cursor = d;
        break;
      }
    }
  }
  return out;
}

export function formatDateIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const dayNames = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
export function formatDateDe(d: Date): string {
  return `${dayNames[d.getDay()]}, ${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}
